import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, recipientApi, type DropboxView } from '../lib/api'
import { formatBytes, formatDate } from '../lib/format'
import { useDocumentTitle } from '../lib/useDocumentTitle'
import { SectionCard } from '../components/SectionCard'
import { FileSection } from '../components/FileSection'
import { LexicalSection } from '../components/LexicalSection'
import { ReadyBar } from '../components/ReadyBar'
import { Alert, Countdown, PageShell, Spinner } from '../components/ui'

export function Recipient() {
  const { token = '' } = useParams()
  const qc = useQueryClient()
  const [missing, setMissing] = useState<string[]>([])
  const [confirmationNote, setConfirmationNote] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['dropbox', token],
    queryFn: () => recipientApi.view(token),
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
  })

  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['dropbox', token] })
  }, [qc, token])

  const ready = useMutation({
    mutationFn: () => recipientApi.ready(token),
    onSuccess: (res) => {
      setMissing([])
      setConfirmationNote(
        `The owner is notified ${res.confirmationDelay} from now. Unready before then and no mail is sent.`,
      )
      refresh()
    },
    onError: (err) => {
      // 422 carries the exact sections that are still empty.
      if (err instanceof ApiError && err.code === 'incomplete') {
        const details = err.details as { missingSectionIds?: string[] } | undefined
        setMissing(details?.missingSectionIds ?? [])
      }
    },
  })

  const unready = useMutation({
    mutationFn: () => recipientApi.unready(token),
    onSuccess: () => {
      setConfirmationNote(null)
      refresh()
    },
  })

  // The plain title, with no suffix — this is the dropbox itself, and the
  // "(admin)" on /a/<token> is what marks that one out as the owner's copy.
  // Called before the early returns; hooks cannot live below them.
  useDocumentTitle(query.data?.dropbox.title ?? null)

  if (query.isPending) {
    return (
      <PageShell>
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Spinner /> Loading…
        </div>
      </PageShell>
    )
  }

  if (query.isError) {
    const err = query.error
    const gone = err instanceof ApiError && err.status === 410
    return (
      <PageShell>
        <Alert tone={gone ? 'warn' : 'error'} title={gone ? 'This link has expired' : 'Cannot open this dropbox'}>
          {err instanceof ApiError ? err.message : 'Unexpected error.'}
        </Alert>
      </PageShell>
    )
  }

  return (
    <Content
      view={query.data}
      token={token}
      missing={missing}
      confirmationNote={confirmationNote}
      busy={ready.isPending || unready.isPending}
      submitError={ready.error instanceof ApiError && ready.error.code !== 'incomplete' ? ready.error.message : null}
      onReady={() => ready.mutate()}
      onUnready={() => unready.mutate()}
      onChanged={refresh}
    />
  )
}

function Content({
  view,
  token,
  missing,
  confirmationNote,
  busy,
  submitError,
  onReady,
  onUnready,
  onChanged,
}: {
  view: DropboxView
  token: string
  missing: string[]
  confirmationNote: string | null
  busy: boolean
  submitError: string | null
  onReady: () => void
  onUnready: () => void
  onChanged: () => void
}) {
  const { dropbox, sections, files, texts, quota, phase, readiness, maxFiles } = view
  // Counted across the whole dropbox: the cap is per dropbox, not per section.
  const freeSlots = maxFiles > 0 ? Math.max(0, maxFiles - files.length) : Number.MAX_SAFE_INTEGER
  const locked = phase !== 'open'
  const isReady = dropbox.status === 'ready'

  const filesBySection = useMemo(() => {
    const map = new Map<string, typeof files>()
    for (const f of files) map.set(f.sectionId, [...(map.get(f.sectionId) ?? []), f])
    return map
  }, [files])

  const textBySection = useMemo(
    () => new Map(texts.map((t) => [t.sectionId, t])),
    [texts],
  )

  const satisfied = useMemo(
    () => new Map(readiness.sections.map((s) => [s.sectionId, s.satisfied])),
    [readiness],
  )

  return (
    <PageShell>
      <header className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{dropbox.title}</h1>
        {dropbox.description && (
          <p className="mt-2 text-zinc-600 dark:text-zinc-400">{dropbox.description}</p>
        )}
        <dl className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-500">
          <div>
            <Countdown iso={dropbox.softDeadline} label="Uploads close:" />{' '}
            <span className="text-zinc-400">({formatDate(dropbox.softDeadline)})</span>
          </div>
          <div>
            <Countdown iso={dropbox.hardDeadline} label="Link expires:" />{' '}
            <span className="text-zinc-400">({formatDate(dropbox.hardDeadline)})</span>
          </div>
          <div>Limit {formatBytes(quota.maxBytes)}</div>
        </dl>
      </header>

      {phase === 'soft_closed' && (
        <div className="mb-6">
          <Alert tone="warn" title="Uploads are closed">
            The upload deadline passed on {formatDate(dropbox.softDeadline)}. You can still view and
            download what was submitted until {formatDate(dropbox.hardDeadline)}.
          </Alert>
        </div>
      )}

      {submitError && (
        <div className="mb-6">
          <Alert tone="error" title="Could not submit">
            {submitError}
          </Alert>
        </div>
      )}

      {missing.length > 0 && (
        <div className="mb-6">
          <Alert tone="warn" title="Some required sections are still empty">
            The highlighted sections below need an answer before you can submit.
          </Alert>
        </div>
      )}

      <div className="space-y-4">
        {sections.map((section, i) => (
          <SectionCard
            key={section.id}
            index={i + 1}
            title={section.title}
            description={section.description}
            required={section.required}
            satisfied={satisfied.get(section.id) ?? false}
            highlighted={missing.includes(section.id)}
          >
            {section.kind === 'files' ? (
              <FileSection
                token={token}
                section={section}
                files={filesBySection.get(section.id) ?? []}
                freeBytes={quota.freeBytes}
                freeSlots={freeSlots}
                readOnly={locked}
                isAdmin={false}
                onChanged={onChanged}
              />
            ) : (
              <LexicalSection
                key={`${section.id}-${locked}`}
                initialState={textBySection.get(section.id)?.lexicalJson ?? null}
                readOnly={locked}
                placeholder={section.description || 'Write your answer…'}
                onSave={async (json, plain) => {
                  await recipientApi.saveText(token, section.id, json, plain)
                  onChanged()
                }}
              />
            )}
          </SectionCard>
        ))}
      </div>

      <ReadyBar
        readiness={readiness}
        quota={quota}
        isReady={isReady}
        busy={busy}
        disabled={locked}
        confirmationNote={confirmationNote}
        onReady={onReady}
        onUnready={onUnready}
      />
    </PageShell>
  )
}
